import { createContext, ReactNode, useEffect, useMemo, useState } from "react";
import { IntercomProvider, useIntercom } from "react-use-intercom";

import { AUTH_INTERCOM_DETAILS } from "../constants/localStorageKeys";
import { useAuth } from "../hooks/useAuth";
import { useIntercomCustom } from "../hooks/useIntercomCustom";
import { safeStorage, safeJsonParse } from "../utils/storage";

type IntercomContextData = {
  isIntercomOpen: boolean;
  setIsIntercomOpen: React.Dispatch<React.SetStateAction<boolean>>;
};

type IntercomProviderProps = {
  children: ReactNode;
};

export const IntercomContext = createContext({} as IntercomContextData);

function Intercom() {
  const { boot, isOpen, show, hide } = useIntercom();
  const { isAuthenticated } = useAuth();
  const { isIntercomOpen, setIsIntercomOpen } = useIntercomCustom();

  const bootIntercom = () => {
    const intercomDetails = safeStorage.getItem(AUTH_INTERCOM_DETAILS);

    if (intercomDetails !== null) {
      const intercomDetailsParsed = safeJsonParse<any>(intercomDetails, null);
      // EN-5270: the API computes the identity verification hash; the secret never reaches the
      // browser. Sessions stored before that change have no userHash, so skip booting until the
      // next login rather than sending an unverified identity.
      if (!intercomDetailsParsed?.userHash) return;

      boot({
        email: intercomDetailsParsed.userEmail ?? undefined,
        horizontalPadding: 60,
        userHash: intercomDetailsParsed.userHash,
        userId: intercomDetailsParsed.userId,
        customAttributes: {
          hospital: intercomDetailsParsed.hospitalGroupName,
          "Hospital ID": intercomDetailsParsed.hospitalGroupId,
          staffID: intercomDetailsParsed.staffId,
        },
        hideDefaultLauncher: true,
      });
    }
  };

  useEffect(() => {
    if (isAuthenticated) {
      bootIntercom();
    }
  }, [isAuthenticated]);

  const shutdownIntercom = () => {
    hide();
    setIsIntercomOpen(false);
  };

  useEffect(() => {
    if (!isOpen && isAuthenticated) {
      shutdownIntercom();
    }
  }, [isOpen]);

  useEffect(() => {
    if (isIntercomOpen) {
      show();
    }

    if (!isIntercomOpen && isAuthenticated) {
      shutdownIntercom();
    }
  }, [isIntercomOpen]);

  return <div />;
}

export function IntercomCustomProvider({ children }: IntercomProviderProps) {
  const [isIntercomOpen, setIsIntercomOpen] = useState(false);

  const values = useMemo(() => {
    return {
      isIntercomOpen,
      setIsIntercomOpen,
    };
  }, [isIntercomOpen]);

  return (
    <IntercomContext.Provider
      // eslint-disable-next-line react/jsx-no-constructed-context-values
      value={values}
    >
      <IntercomProvider
        appId={String(process.env.REACT_APP_INTERCOM_APP_ID)}
        autoBoot={false}
      >
        <Intercom />
      </IntercomProvider>
      {children}
    </IntercomContext.Provider>
  );
}
